import React, { useState, useEffect, useRef } from 'react';
import {
    ChevronDown, Bell, Folder, Building, 
    PlusCircle,  CheckCircle,  Menu, 
    File as FileIcon,  Check, Search, ChevronRight,  Command, PanelRightClose, PanelRightOpen
} from 'lucide-react';
import { useDispatch } from 'react-redux';
import { switchUser } from '../redux/features/users/userSlice';
// import Logo from '../images/tecorelabslogo.png'; // Assuming this is used elsewhere in your app

// User Switcher Dropdown
const UserSwitcher = ({ users, currentUser, setCurrentUser }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const dispatch = useDispatch();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const changeUser = async (user) => {
        dispatch(switchUser(user));
        setCurrentUser(user);
        setIsOpen(false);
    }

    return (
        <div className="relative" ref={dropdownRef}>
            <button onClick={() => setIsOpen(!isOpen)} className="flex items-center space-x-2">
                <img className="h-9 w-9 rounded-full object-cover" src={currentUser?.avatarUrl} alt="User avatar" />
                <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-slate-800">{currentUser?.name}</p>
                    <p className="text-xs font-medium text-slate-500">{currentUser?.role}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-500 ml-1 hidden sm:block" />
            </button>
            {isOpen && (
                <div className="absolute right-0 mt-2 w-60 bg-white rounded-lg shadow-lg z-20 border border-slate-200">
                    <div className="p-2">
                        <p className="px-3 py-1 text-xs font-semibold text-slate-400">Switch User</p>
                        {users?.map(user => (
                            <a
                                key={user.id}
                                href="#"
                                // onClick={(e) => {
                                //     e.preventDefault();
                                //     setCurrentUser(user);
                                //     switchUser(user);
                                //     setIsOpen(false);
                                // }}
                                onClick={() => changeUser(user)}
                                className={`block w-full text-left px-3 py-2 text-sm rounded-md ${currentUser?.id === user.id ? 'bg-slate-100' : 'hover:bg-slate-100'}`}
                            >
                                <div className="flex items-center">
                                    <img src={user.avatarUrl} className="w-8 h-8 rounded-full mr-3" alt={user.name} />
                                    <div>
                                        <p className="font-semibold text-slate-800">{user.name}</p>
                                        <p className="text-xs text-slate-500">{user.role}</p>
                                    </div>
                                    {currentUser?.id === user.id && <CheckCircle className="w-4 h-4 ml-auto text-indigo-600" />}
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Language Switcher Component
const LanguageSwitcher = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedLang, setSelectedLang] = useState({
        code: 'EN',
        name: 'English',
        flag: '🇺🇸'
    });

    const languages = [
        { code: 'EN', name: 'English', flag: '🇺🇸' },
        { code: 'AR', name: 'Arabic', flag: '🇸🇦' },
        { code: 'ZH', name: 'Chinese', flag: '🇨🇳' }
    ];

    const dropdownRef = useRef(null);

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-slate-100 transition-colors border border-transparent hover:border-slate-200"
            >
                <span className="text-xl leading-none">{selectedLang.flag}</span>
                <span className="text-sm font-medium text-slate-700 hidden md:block">{selectedLang.code}</span>
                <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white border border-slate-200 rounded-xl shadow-xl z-50 py-1 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="px-3 py-1.5 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                        Select Language
                    </div>
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            onClick={() => {
                                setSelectedLang(lang);
                                setIsOpen(false);
                                localStorage.setItem('lang', lang?.name ?? 'English')
                            }}
                            className={`w-full flex items-center px-4 py-2 text-sm transition-colors ${selectedLang.code === lang.code
                                ? 'bg-indigo-50 text-indigo-700 font-medium'
                                : 'text-slate-700 hover:bg-slate-50'
                                }`}
                        >
                            <span className="mr-3 text-lg">{lang.flag}</span>
                            <span>{lang.name}</span>
                            {selectedLang.code === lang.code && (
                                <Check className="ml-auto h-4 w-4" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// Organization Switcher Component (Redesigned based on screenshot)
const OrgSwitcher = ({ selectedOrg, setSelectedOrg, onNewOrgClick, organizations }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter organizations based on search query
    const filteredOrgs = organizations?.filter(org =>
        org.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Main Toggle Button - Light Theme matching screenshot structure */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between w-56 sm:w-64 p-2 bg-white border border-slate-200 rounded-2xl hover:border-indigo-300 hover:bg-slate-50 transition-all shadow-sm group"
            >
                <div className="flex items-center gap-3 overflow-hidden">
                    <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl group-hover:scale-105 transition-transform shrink-0">
                        <Building size={18} strokeWidth={2.5} />
                    </div>
                    <div className="flex flex-col items-start truncate">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-none mb-1">Active Organization</span>
                        <span className="text-sm font-bold text-slate-900 truncate w-full text-left">{selectedOrg?.name || 'Select Org'}</span>
                    </div>
                </div>
                <div className="pl-2 shrink-0">
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </div>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                    
                    {/* Search Bar */}
                    <div className="p-3 border-b border-slate-100 bg-slate-50/50">
                        <div className="relative flex items-center">
                            <Search className="absolute left-3 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search orgs..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-white border border-slate-200 rounded-xl py-2 pl-9 pr-3 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-sm placeholder:text-slate-400 placeholder:font-medium"
                            />
                        </div>
                    </div>

                    {/* Organizations List */}
                    <div className="max-h-60 overflow-y-auto p-2 custom-scrollbar space-y-1 bg-white">
                        {filteredOrgs?.length > 0 ? (
                            filteredOrgs.map(org => {
                                const isSelected = org.id === selectedOrg?.id;
                                return (
                                    <button
                                        key={org.id}
                                        onClick={() => {
                                            setSelectedOrg(org);
                                            setIsOpen(false);
                                            setSearchQuery(''); // Reset search on select
                                        }}
                                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-bold transition-all ${
                                            isSelected
                                                ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200/50'
                                                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                                        }`}
                                    >
                                        <span className="truncate">{org.name}</span>
                                        {isSelected && <Check className="w-4 h-4 shrink-0" strokeWidth={3} />}
                                    </button>
                                );
                            })
                        ) : (
                            <div className="py-6 text-center flex flex-col items-center justify-center">
                                <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center mb-2">
                                    <Folder className="w-5 h-5 text-slate-300" />
                                </div>
                                <span className="text-sm font-bold text-slate-500">No orgs found.</span>
                            </div>
                        )}
                    </div>

                    {/* Footer Action */}
                    <div className="p-2 border-t border-slate-100 bg-slate-50">
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                onNewOrgClick();
                            }}
                            className="w-full flex items-center justify-center gap-2 py-2.5 text-[11px] font-black tracking-widest uppercase text-indigo-600 hover:text-white hover:bg-indigo-600 rounded-xl transition-all border border-transparent hover:shadow-md hover:shadow-indigo-200"
                        >
                            <PlusCircle className="w-4 h-4" strokeWidth={2.5} />
                            Create New Org
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// Header Component
const Header = ({ users, selectedProject, currentUser, setCurrentUser, isSidebarOpen, setIsSidebarOpen, activeNav, selectedOrg, setSelectedOrg, onNewOrgClick, organizations }) => {
    return (
        <header className="h-20 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-4 sm:px-8 sticky top-0 z-40 shrink-0">
            {/* Left Section */}
            <div className="flex items-center gap-4">
                <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg">
                    {/* <Menu size={24} /> */}
                  { !isSidebarOpen? <PanelRightClose size={18}/>:<PanelRightOpen size={18}/>}
                </button>
                <div className="hidden sm:flex items-center gap-2 text-sm text-slate-500">
                    <Command size={16} className="text-indigo-600" />
                    <span className="font-bold text-slate-400 uppercase tracking-widest text-[10px]">Active Project</span>
                    <ChevronRight size={14} />
                    <span className="font-bold text-slate-900 truncate max-w-[200px]">{selectedProject?.name || 'No Project'}</span>
                </div>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-2 sm:gap-4">
                <OrgSwitcher
                    selectedOrg={selectedOrg}
                    setSelectedOrg={setSelectedOrg}
                    onNewOrgClick={onNewOrgClick}
                    organizations={organizations}
                />
                
                {/* <div className="hidden lg:block">
                    <LanguageSwitcher />
                </div> */}
                
                <div className="h-10 w-px bg-slate-200 mx-2 hidden lg:block"></div>
                
                <button className="relative p-2 sm:p-3 text-slate-500 hover:bg-slate-100 hover:text-indigo-600 rounded-2xl transition-all hover:scale-105 active:scale-95 group">
                    <Bell size={22} />
                    <span className="absolute top-2 right-2 sm:top-3 sm:right-3 w-2.5 h-2.5 bg-rose-500 border-[3px] border-white rounded-full group-hover:scale-110 transition-transform"></span>
                </button>
                
                <UserSwitcher
                    users={users}
                    currentUser={currentUser}
                    setCurrentUser={setCurrentUser}
                />
            </div>
        </header>
    );
};

export default Header;